import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ROOT } from '../service/Services';
import DataTable from 'react-data-table-component';
import {
  HiOutlineMail,
  HiOutlineMailOpen,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineEye,
  HiOutlineDocumentDownload,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineX
} from 'react-icons/hi';


export const InvoiceGenerateWithoutpo = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerItems, setCustomerItems] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState('');
  const today = new Date().toISOString().split("T")[0];
  const [deliveryDate, setDeliveryDate] = useState(today);
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([
    { itemId: '', itemName: '', quantity: '', unit: '', stock: 0, rate: 0 }
  ]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [selectedItemDetails, setSelectedItemDetails] = useState({});
  const [saving, setSaving] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [dnLoading, setDnLoading] = useState(false);
  const [dnPage, setDnPage] = useState(1);
  const [dnTotalRows, setDnTotalRows] = useState(0);

  // Email functionality states
  const [emailModal, setEmailModal] = useState({ visible: false, data: null });
  const [emailForm, setEmailForm] = useState({
    recipients: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Fetch customers and sales persons on mount
  useEffect(() => {
    axios.get(`${API_ROOT}/customer`, {
      headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => setCustomers(res.data?.data || []))
      .catch(() => setCustomers([]));
    axios.get(`${API_ROOT}/sales`, {
      headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => setSalesPersons(res.data?.data || []))
      .catch(() => setSalesPersons([]));
  }, []);

  // Fetch items for selected customer (or show all items)
  useEffect(() => {
    if (selectedCustomer) {
      fetchItems('');
    } else {
      setCustomerItems([]);
    }
    setItems([{ itemId: '', itemName: '', quantity: '', unit: '' }]);
  }, [selectedCustomer]);

  // Fetch delivery notes
  const fetchDeliveryNotes = async (page = 1) => {
    setDnLoading(true);
    try {
      const res = await axios.get(`${API_ROOT}/delivery-note`, {
        params: { page, limit: 10 },
        headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDeliveryNotes(res.data?.data || []);
      setDnTotalRows(res.data?.totalItems || 0);
      setDnPage(res.data?.currentPage || 1);
    } catch {
      setDeliveryNotes([]);
    } finally {
      setDnLoading(false);
    }
  };

  // Item search handler
  const fetchItems = (search) => {
    setLoadingItems(true);
    setItemSearchLoading(true);
    axios.get(`${API_ROOT}/items`, {
      params: { name: search },
      headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => {
        // Ensure data is always an array
        const arr = Array.isArray(res.data?.data) ? res.data.data : [];
        setCustomerItems(arr);
      })
      .catch(() => setCustomerItems([]))
      .finally(() => {
        setLoadingItems(false);
        setItemSearchLoading(false);
      });
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedCustomer) fetchItems(itemSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [itemSearch, selectedCustomer]);

  const handleItemSelect = (idx, itemId) => {
    const itemObj = customerItems.find(i => i._id === itemId);
    const updated = items.map((item, i) =>
      i === idx
        ? {
          ...item,
          itemId,
          itemName: itemObj ? itemObj.itemName : '',
          unit: itemObj ? itemObj.unit : '',
          itemCode: itemObj ? itemObj.itemCode : '',
          hsnCode: itemObj ? itemObj.hsnCode : '',
          purchaseRate: itemObj ? itemObj.purchaseRate : '',
          saleRate: itemObj ? itemObj.saleRate : '',
          taxRate: itemObj ? itemObj.taxRate : '',
          stock: itemObj ? Number(itemObj.stock || 0) : 0,
          rate: itemObj ? Number(itemObj.saleRate || 0) : 0
        }
        : item
    );
    setItems(updated);
    setSelectedItemDetails(prev => ({ ...prev, [idx]: itemObj || {} }));
  };

  const handleItemChange = (idx, field, value) => {
    const updated = items.map((item, i) => {
      if (i !== idx) return item;
      let next = { ...item, [field]: value };
      if (field === 'quantity') {
        const qtyNum = Math.max(0, Number(value || 0));
        const capped = item.stock ? Math.min(qtyNum, Number(item.stock)) : qtyNum;
        next.quantity = String(capped);
      }
      return next;
    });
    setItems(updated);
  };

  const addItemRow = () => {
    setItems([...items, { itemId: '', itemName: '', quantity: '', unit: '', stock: 0, rate: 0 }]);
  };

  const removeItemRow = idx => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedCustomer || !selectedSalesPerson || !deliveryDate || items.length === 0) {
      alert('Please fill all required fields and add at least one item.');
      return;
    }
    // Validate stock and quantities
    for (const line of items) {
      if (!line.itemId) continue;
      const qty = Number(line.quantity || 0);
      const stock = Number(line.stock || 0);
      if (qty <= 0) {
        alert(`Quantity must be greater than 0 for item ${line.itemName}`);
        return;
      }
      if (stock && qty > stock) {
        alert(`Insufficient stock for ${line.itemName}. Available: ${stock}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        customerId: selectedCustomer,
        salesPersonId: selectedSalesPerson,
        deliveryDate,
        remarks,
        items: items
          .filter(it => it.itemId && it.quantity)
          .map(it => ({
            itemId: it.itemId,
            quantity: Number(it.quantity),
            rate: Number(it.rate || it.saleRate || 0)
          }))
      };
      const res = await axios.post(`${API_ROOT}/delivery-note`, payload, {
        headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data?.success) {
        alert('Delivery Note Saved & Approval Email Sent!');
        // Refresh delivery notes list
        fetchDeliveryNotes();
        // Optionally reset form
        setSelectedCustomer('');
        setSelectedSalesPerson('');
        setDeliveryDate('');
        setRemarks('');
        setItems([{ itemId: '', itemName: '', quantity: '', unit: '' }]);
      } else {
        alert(res.data?.message || 'Failed to save delivery note');
      }
    } catch (err) {
      alert('Error saving delivery note');
    } finally {
      setSaving(false);
    }
  };

  // Email functionality
  const openEmailModal = (row) => {
    const customer = row.customerId;
    const defaultSubject = `Delivery Note ${row.noteNumber} - ${customer?.name || 'Customer'}`;
    const defaultMessage = `Dear ${customer?.name || 'Customer'},\n\nPlease find attached the delivery note ${row.noteNumber}.\n\nBest regards,\nYour Company Team`;

    setEmailForm({
      recipients: customer?.email || '',
      subject: defaultSubject,
      message: defaultMessage
    });
    setEmailModal({ visible: true, data: row });
  };

  const sendEmail = async () => {
    if (!emailForm.recipients.trim()) {
      alert('Please enter recipient email addresses');
      return;
    }

    try {
      setSendingEmail(true);
      const recipients = emailForm.recipients.split(',').map(email => email.trim());

      const response = await axios.post(
        `${API_ROOT}/delivery-note/${emailModal.data._id}/send-email`,
        {
          recipients,
          subject: emailForm.subject,
          message: emailForm.message
        },
        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.data.success) {
        alert('Email sent successfully!');
        setEmailModal({ visible: false, data: null });
        setEmailForm({ recipients: '', subject: '', message: '' });
        // Refresh the list to update mail status
        fetchDeliveryNotes(dnPage);
      } else {
        alert('Failed to send email: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email: ' + (error.response?.data?.message || error.message));
    } finally {
      setSendingEmail(false);
    }
  };

  const dnColumns = [
    {
      name: 'Date',
      selector: row => row.deliveryDate ? new Date(row.deliveryDate).toLocaleDateString() : '',
      width: '100px'
    },
    {
      name: 'Note No.',
      selector: row => row.noteNumber || '',
      width: '120px',
      cell: row => (
        <span className="font-semibold text-blue-600">{row.noteNumber}</span>
      )
    },
    {
      name: 'Customer',
      selector: row => row.customerId?.name || '',
      width: '150px'
    },
    {
      name: 'Sales Person',
      selector: row => row.salesPersonId?.name || '',
      width: '150px'
    },
    {
      name: 'Status',
      cell: row => {
        const status = row.status || 'Draft';
        const isApproved = row.isApproved;

        if (isApproved) {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <HiOutlineCheckCircle className="mr-1" size={12} />
              Approved
            </span>
          );
        }

        switch (status.toLowerCase()) {
          case 'pendingapproval':
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <HiOutlineClock className="mr-1" size={12} />
                Pending
              </span>
            );
          case 'submitted':
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Submitted
              </span>
            );
          case 'cancelled':
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Cancelled
              </span>
            );
          default:
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Draft
              </span>
            );
        }
      },
      width: '120px'
    },
    {
      name: 'Mail Status',
      cell: row => {
        const mailSent = row.mailSent || false;

        if (mailSent) {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <HiOutlineMailOpen className="mr-1" size={12} />
              Sent
            </span>
          );
        } else {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              <HiOutlineMail className="mr-1" size={12} />
              Not Sent
            </span>
          );
        }
      },
      width: '100px'
    },
    {
      name: 'Remarks',
      selector: row => row.remarks || '',
      width: '150px'
    },
    {
      name: 'Items',
      cell: row => (
        <div className="text-xs">
          {row.items?.map((it, idx) => (
            <div key={idx} className="mb-1">
              {it.itemId?.itemName} ({it.quantity})
            </div>
          ))}
        </div>
      ),
      width: '180px'
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open(`/delivery-note-approval/${row._id}`, '_blank')}
            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
            title="View Details"
          >
            <HiOutlineEye size={16} />
          </button>
          <button
            onClick={() => openEmailModal(row)}
            className={`p-1 rounded ${row.mailSent ? 'text-green-600 hover:text-green-800 hover:bg-green-50' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}
            title={row.mailSent ? 'Resend Email' : 'Send Email'}
          >
            <HiOutlineMail size={16} />
          </button>
        </div>
      ),
      width: '100px'
    }
  ];

  return (
<div className="min-h-screen bg-gray-50">
  <div className="max-w-6xl mx-auto px-1 sm:px-2 md:px-4 lg:px-6">

    {/* Header */}
    <div className="bg-white rounded-lg shadow p-2 sm:p-3 md:p-4 mb-4 sm:mb-6 border border-gray-200">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 sm:gap-3 md:gap-4 mb-2 sm:mb-4">
        <div>
          <h1 className="text-base max-[400px]:text-sm sm:text-lg md:text-2xl font-bold text-gray-900">
            Create Delivery Note
          </h1>
          <p className="text-gray-600 text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm mt-1">
            Only in-stock items can be delivered
          </p>
        </div>
        <button
          onClick={fetchDeliveryNotes}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 
                     w-full md:w-auto justify-center text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm lg:text-base"
        >
          <HiOutlineEye size={14} /> View All Notes
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">

        {/* Customer & Sales Person */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
          <div>
            <label className="block text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm font-medium text-gray-700 mb-1">
              Customer *
            </label>
            <select
              value={selectedCustomer}
              onChange={e => setSelectedCustomer(e.target.value)}
              required
              className="w-full px-1 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 
                         text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
            >
              <option value="">Select Customer</option>
              {customers?.map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm font-medium text-gray-700 mb-1">
              Sales Person *
            </label>
            <select
              value={selectedSalesPerson}
              onChange={e => setSelectedSalesPerson(e.target.value)}
              required
              className="w-full px-1 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 
                         text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
            >
              <option value="">Select Sales Person</option>
              {salesPersons?.map(s => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Delivery Date & Remarks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
          <div>
            <label className="block text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm font-medium text-gray-700 mb-1">
              Delivery Date *
            </label>
            <input
              type="date"
              value={deliveryDate}
              onChange={e => setDeliveryDate(e.target.value)}
              required
              className="w-full px-1 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 
                         text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              rows={2}
              placeholder="Any remarks"
              className="w-full px-1 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 
                         text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
            />
          </div>
        </div>

        {/* Items Section */}
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-2 mb-2">
            <label className="text-sm max-[400px]:text-[11px] sm:text-base md:text-lg font-semibold text-gray-900">Items *</label>
            <button
              type="button"
              onClick={addItemRow}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 
                         w-full sm:w-auto justify-center text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
            >
              <HiOutlinePlus size={14} /> Add Item
            </button>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto w-full">
            <table className="min-w-full border border-gray-200 rounded-lg text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {["Item", "Qty", "Unit", "Rate", "Total", "Action"].map((h, i) => (
                    <th
                      key={i}
                      className="px-1 sm:px-2 md:px-3 py-1 sm:py-2 text-left font-medium text-gray-500 uppercase whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-1 sm:px-2 md:px-3 py-1 sm:py-2">
                      <select
                        value={item.itemId}
                        onChange={e => handleItemSelect(idx, e.target.value)}
                        required
                        className="w-full px-1 sm:px-2 py-1 border border-gray-300 rounded text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
                      >
                        <option value="">Select Item</option>
                        {customerItems.filter(i => Number(i.stock || 0) > 0).map(i => (
                          <option key={i._id} value={i._id}>{i.itemName} (Stock: {i.stock})</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-1 sm:px-2 md:px-3 py-1 sm:py-2">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                        required
                        min={1}
                        className="w-12 max-[400px]:w-10 sm:w-16 md:w-20 px-1 sm:px-2 py-1 border border-gray-300 rounded text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
                      />
                      {item.stock > 0 && (
                        <div className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 mt-1">
                          Stock: {item.stock}
                        </div>
                      )}
                    </td>
                    <td className="px-1 sm:px-2 md:px-3 py-1 sm:py-2">
                      <input
                        type="text"
                        value={item.unit}
                        readOnly
                        className="w-10 max-[400px]:w-9 sm:w-14 md:w-20 px-1 sm:px-2 py-1 border border-gray-300 rounded text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm bg-gray-50"
                      />
                    </td>
                    <td className="px-1 sm:px-2 md:px-3 py-1 sm:py-2">
                      <input
                        type="number"
                        value={item.rate}
                        onChange={e => handleItemChange(idx, 'rate', e.target.value)}
                        required
                        min={0}
                        step="0.01"
                        className="w-14 max-[400px]:w-12 sm:w-20 md:w-24 px-1 sm:px-2 py-1 border border-gray-300 rounded text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
                      />
                    </td>
                    <td className="px-1 sm:px-2 md:px-3 py-1 sm:py-2 font-medium">
                      ₹{(Number(item.rate || 0) * Number(item.quantity || 0)).toFixed(2)}
                    </td>
                    <td className="px-1 sm:px-2 md:px-3 py-1 sm:py-2">
                      {items.length > 1 && (
                        <button type="button" onClick={() => removeItemRow(idx)} className="text-red-600 hover:text-red-800 p-1">
                          <HiOutlineTrash size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div className="mt-2 sm:mt-3 md:mt-4 text-right text-[11px] max-[400px]:text-[10px] sm:text-sm md:text-base lg:text-lg font-bold text-gray-900">
            Total: ₹{items.reduce((sum, it) => sum + (Number(it.rate || 0) * Number(it.quantity || 0)), 0).toFixed(2)}
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center mt-3 sm:mt-4">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed 
                       gap-1 sm:gap-2 justify-center text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm"
          >
            {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : 'Create Delivery Note & Send Approval Email'}
          </button>
        </div>
      </form>
    </div>

    {/* Recent Delivery Notes */}
    <div className="bg-white rounded-lg shadow p-2 sm:p-3 md:p-4 mt-4 sm:mt-6 border border-gray-200 overflow-x-auto text-[10px] max-[400px]:text-[9px] sm:text-xs md:text-sm">
      <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-2 sm:mb-3 md:mb-4">Recent Delivery Notes</h2>
      <DataTable
        columns={dnColumns}
        data={deliveryNotes}
        pagination
        paginationServer
        paginationTotalRows={dnTotalRows}
        onChangePage={fetchDeliveryNotes}
        paginationPerPage={10}
        progressPending={dnLoading}
        highlightOnHover
        responsive
        customStyles={{
          headCells: {
            style: {
              backgroundColor: '#f8fafc',
              color: '#475569',
              fontWeight: 600,
              fontSize: '10px'
            }
          }
        }}
      />
    </div>
  </div>
</div>


  );
}

export default InvoiceGenerateWithoutpo;
